import type { ColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';

import {
  ArrowExpandIcon,
  DefaultInfluencerIcon,
  DiamondIcon,
  MoneyIcon,
} from '../../../assets';
import type {
  ProposalStatusType,
  ProposalTable,
  ProposalUserInfo,
} from '../../../types/proposal';
import { formatNumberMoney } from '../../../utils/numberFormatter';
import { StatusText } from '../../common/StatusText';
import { UsersImages } from '../../images/UserImages';
import DTBoldCell from '../DTBoldCell';
import { DTCell } from '../DTCell';
import DTHeader from '../DTHeader';

type GetProposalsTableHeadersProps = {
  columnHelper: ColumnHelper<ProposalTable>;
  handleChangeSortDirection: (id: string) => void;
  t: TFunction;
  extras: {};
};

export const getProposalsTableHeaders = ({
  columnHelper,
  handleChangeSortDirection,
  t,
}: GetProposalsTableHeadersProps) => [
  columnHelper.accessor('name', {
    size: 200,
    minSize: 150,
    maxSize: 300,
    cell: (info) => {
      if (!info.row.getCanExpand()) {
        return <DTBoldCell content={info.getValue()} id={info.cell.id} />;
      }
      return (
        <div className="flex flex-row">
          <button
            className={`flex items-center justify-center ${info.row.getIsExpanded() ? 'rotate-90' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              info.row.getToggleExpandedHandler()();
            }}
            style={{ cursor: 'pointer' }}
          >
            <ArrowExpandIcon />
          </button>
          <DTBoldCell content={info.getValue()} id={info.cell.id} />
        </div>
      );
    },
    header: ({ header, ...info }) => (
      <DTHeader
        sortable
        first
        onClick={() => handleChangeSortDirection(header.id)}
        title={t('proposals.header.name')}
        sorting={(info as any).orderBy}
        sortingDirection={(info as any).orderDirection}
        accessor="name"
      />
    ),
  }),
  columnHelper.accessor('ProposalUsers', {
    id: 'influencers',
    size: 150,
    minSize: 100,
    maxSize: 200,
    header: () => (
      <DTHeader
        onClick={() => {}}
        title={t('proposals.header.influencers')}
        accessor="influencers"
      />
    ),
    cell: (info) => {
      const users = info.getValue() as ProposalUserInfo[];
      return (
        <UsersImages
          size="size-[35px]"
          userCount={users?.length || 0}
          userImages={users?.map((u) => u.profile_picture || '') || []}
        />
      );
    },
  }),
  columnHelper.accessor('status', {
    size: 170,
    minSize: 130,
    maxSize: 220,
    header: () => (
      <DTHeader
        onClick={() => {}}
        title={t('proposals.header.status')}
        accessor="status"
      />
    ),
    cell: (info) => (
      <StatusText
        type="proposal"
        status={info.getValue() as ProposalStatusType}
      />
    ),
  }),
  columnHelper.accessor('money', {
    size: 130,
    minSize: 90,
    maxSize: 160,
    header: ({ header, ...info }) => (
      <DTHeader
        sortable
        onClick={() => handleChangeSortDirection(header.id)}
        title={t('proposals.header.amount')}
        sorting={(info as any).orderBy}
        sortingDirection={(info as any).orderDirection}
        accessor="money"
      />
    ),
    cell: (info) => {
      const val = info.getValue();
      const currency = info.row.original.currency || '€';
      return (
        <DTCell
          text={val != null ? formatNumberMoney(val, undefined, currency) : '-'}
          icon={<MoneyIcon />}
        />
      );
    },
  }),
  columnHelper.accessor('month', {
    size: 140,
    minSize: 100,
    maxSize: 180,
    header: ({ header, ...info }) => (
      <DTHeader
        sortable
        onClick={() => handleChangeSortDirection(header.id)}
        title={t('proposals.header.month')}
        sorting={(info as any).orderBy}
        sortingDirection={(info as any).orderDirection}
        accessor="month"
        last
      />
    ),
    cell: (info) => <DTCell text={info.getValue() || '-'} />,
  }),
];

export const PROPOSAL_TABLE_SUBROWS = {
  influencer: {
    size: 200,
    minSize: 150,
    maxSize: 300,
    cell: (
      name: string,
      id: string,
      profilePicture: string,
      exclusivity?: boolean,
    ) => (
      <div className="flex flex-row gap-x-4 items-center">
        {profilePicture ? (
          <img
            src={`${process.env.REACT_APP_AWS_PUBLIC_ENDPOINT}/${profilePicture}`}
            className="rounded-full size-[35px] object-cover"
          />
        ) : (
          <div className="rounded-full bg-influentia-black size-[35px]">
            <DefaultInfluencerIcon className="size-[inherit]" />
          </div>
        )}
        <DTBoldCell
          content={name}
          id={id}
          badge={
            exclusivity ? (
              <DiamondIcon className="size-[20px] text-influentia-light-blue flex-none" />
            ) : undefined
          }
        />
      </div>
    ),
  },
  amount: {
    size: 130,
    minSize: 90,
    maxSize: 160,
    cell: (val: number | null, currency: string) => (
      <DTCell
        text={val != null ? formatNumberMoney(val, undefined, currency) : '-'}
        icon={<MoneyIcon />}
      />
    ),
  },
};
